import { ErrorEvent } from '@sentry/vue'
import { App } from 'vue'
import { Router } from 'vue-router'

import { useCurrentUser } from '@/shared/composables/current-user'
import { useGuestAuth } from '@/shared/composables/guest-auth'
import { useStoreEnvSettings } from '@/shared/store/environment'
import { appPackageVersion } from '@/shared/store/package-meta'
import { isDevelopment, isElectron, tauriMode } from '@/shared/utils/import-meta'

export default (app: App<Element>, router: Router): App<Element> => {
  if (isDevelopment() || tauriMode() || isElectron()) {
    return app
  }
  const { getSettingByKey, onInitialized } = useStoreEnvSettings()
  onInitialized().then(() => {
    const dsn = getSettingByKey('sentryServer')?.value
    const environment = getSettingByKey('environment')?.value ?? 'Unknown'
    if (dsn) {
      import('@sentry/vue').then(({ init, browserTracingIntegration, replayIntegration }) => {
        init({
          release: appPackageVersion,
          environment,
          app,
          dsn,
          integrations: [
            browserTracingIntegration({
              router,
            }),
            replayIntegration(),
          ],
          // Set tracesSampleRate to 1.0 to capture 100%
          // of transactions for performance monitoring.
          // We recommend adjusting this value in production
          tracesSampleRate: 0.1,
          // This sets the sample rate to be 10%. You may want this to be 100% while
          // in development and sample at a lower rate in production
          replaysSessionSampleRate: 0.1,
          // If the entire session is not sampled, use the below sample rate to sample
          // sessions when an error occurs.
          replaysOnErrorSampleRate: 1.0,
          trackComponents: false,
          beforeSend: (event: ErrorEvent) => {
            const { currentUser } = useCurrentUser()
            const { currentGuest } = useGuestAuth()
            event.contexts = {
              ...event.contexts,
              app: {
                ...event.contexts?.app,
                app_version: appPackageVersion,
              },
            }
            const userId = currentUser.value?.id
            event.user = {
              ...event.user,
              id: userId!,
              username: currentUser.value?.name ?? currentGuest.value?.pServed ?? 'Anonymous',
              segment: currentUser.value ? 'User' : currentGuest.value ? 'Guest' : 'Unauthorized',
            }
            return event
          },
        })
      })
    }
  })
  return app
}
